// OrdersTable.jsx — Column definitions, cell rendering, and the orders data table
// Uses react-virtuoso TableVirtuoso for virtual scrolling (same pattern as MW + Positions)
// Built with Tailwind CSS

import React, { forwardRef, useMemo } from 'react';
import { TableVirtuoso } from 'react-virtuoso';
import { fmtINR, fmtNum } from './calculations';
import CheckboxDropdown from './CheckboxDropdown';
import ResizeGrip from './ResizeGrip';

// ═══════════════════════════════════════════════════════════════
// COLUMN DEFINITIONS
// ═══════════════════════════════════════════════════════════════

export const ORDER_COLUMNS = [
  { key: 'orderTimestamp',  label: 'Time',        align: 'left',   width: 72 },
  { key: 'underlying',     label: 'Underlying',   align: 'left',   width: 140 },
  { key: 'strike',         label: 'Strike',       align: 'right',  width: 68 },
  { key: 'type',           label: 'C/P',          align: 'center', width: 36,  filter: 'type' },
  { key: 'expiryStr',      label: 'Expiry',       align: 'left',   width: 78,  filter: 'expiry' },
  { key: 'transactionType',label: 'B/S',          align: 'center', width: 40,  filter: 'bs' },
  { key: 'orderStatus',    label: 'Status',       align: 'left',   width: 90,  filter: 'status' },
  { key: 'price',          label: 'Price',        align: 'right',  width: 62 },
  { key: 'spot',           label: 'CMP',          align: 'right',  width: 68 },
  { key: 'ltp',            label: 'LTP',          align: 'right',  width: 62 },
  { key: 'bestBuySell',    label: 'Bid/Ask',      align: 'right',  width: 100 },
  { key: 'orderId',        label: 'Order ID',     align: 'left',   width: 120 },
  { key: 'accountLabel',   label: 'Account',      align: 'left',   width: 80,  filter: 'account' },
  { key: 'queuePosition',  label: 'Queue',        align: 'right',  width: 60 },
  // Modify/Cancel/Trades buttons are rendered separately (not in columns array)
];

// ═══════════════════════════════════════════════════════════════
// RENDER CELL
// ═══════════════════════════════════════════════════════════════

function renderCell(col, row, isDark) {
  const base = 'px-1.5 py-1 whitespace-nowrap text-xs-tight';
  const muted = isDark ? 'text-app-text-muted' : 'text-app-text-light-muted';
  const dim = isDark ? 'text-app-text-dim' : 'text-app-text-light-dim';
  const text = isDark ? 'text-app-text' : 'text-app-text-light';

  switch (col.key) {
    case 'orderTimestamp': {
      // Show just the time portion HH:MM:SS
      const ts = row.orderTimestamp || '';
      let display = '';
      if (ts) {
        try {
          const d = new Date(ts);
          if (!isNaN(d.getTime())) {
            display = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
          } else {
            // Maybe it's already a time string from Kite like "2026-03-22 10:15:30"
            const parts = ts.split(' ');
            display = parts.length > 1 ? parts[1] : ts;
          }
        } catch { display = ts; }
      }
      return <td key={col.key} className={`${base} ${muted}`}>{display || '—'}</td>;
    }
    case 'underlying':
      return <td key={col.key} className={`${base} font-semibold ${text} overflow-hidden text-ellipsis`} style={{ maxWidth: col.width }} title={row.underlying || row.symbol}>{row.underlying || row.symbol}</td>;
    case 'strike':
      return <td key={col.key} className={`${base} text-right font-medium ${text}`}>{fmtNum(row.strike)}</td>;
    case 'type':
      return (
        <td key={col.key} className={`${base} text-center`}>
          <span className={`text-[10px] font-bold px-1 py-0.5 rounded ${row.type === 'CE' ? (isDark ? 'bg-[#1a2e1a] text-accent-green' : 'bg-green-100 text-green-600') : (isDark ? 'bg-[#2e1a1a] text-accent-red' : 'bg-red-100 text-red-600')}`}>
            {row.type}
          </span>
        </td>
      );
    case 'expiryStr':
      return <td key={col.key} className={`${base} ${muted}`}>{row.expiryStr || '—'}</td>;
    case 'transactionType':
      return (
        <td key={col.key} className={`${base} text-center`}>
          <span className={`text-[10px] font-bold px-1 py-0.5 rounded ${row.transactionType === 'BUY' ? (isDark ? 'bg-[#1a2e1a] text-accent-green' : 'bg-green-100 text-green-600') : (isDark ? 'bg-[#2e1a1a] text-accent-red' : 'bg-red-100 text-red-600')}`}>
            {row.transactionType}
          </span>
        </td>
      );
    case 'orderStatus': {
      const filled = row.filledQuantity || 0;
      const total = row.quantity || 0;
      const statusText = `${fmtNum(filled)} / ${fmtNum(total)}`;
      let statusColor = muted;
      const s = row.status;
      if (s === 'COMPLETE') statusColor = 'text-accent-green';
      else if (s === 'REJECTED') statusColor = 'text-accent-red';
      else if (s === 'CANCELLED') statusColor = dim;
      else if (s === 'OPEN' || s === 'PENDING' || s === 'PARTIAL') statusColor = 'text-accent-yellow';
      else if (s === 'AMO') statusColor = 'text-accent-blue';
      return (
        <td key={col.key} className={`${base}`}>
          <span className={`font-semibold ${statusColor}`}>{statusText}</span>
          <span className={`ml-1 text-[9px] ${muted}`}>{s}</span>
        </td>
      );
    }
    case 'price':
      return <td key={col.key} className={`${base} text-right font-medium ${text}`}>₹{Number(row.price).toFixed(2)}</td>;
    case 'spot':
      return <td key={col.key} className={`${base} text-right ${muted}`}>{row.spot > 0 ? Number(row.spot).toFixed(2) : '—'}</td>;
    case 'ltp':
      return <td key={col.key} className={`${base} text-right font-semibold ${text}`}>{row.ltp > 0 ? `₹${Number(row.ltp).toFixed(2)}` : '—'}</td>;
    case 'bestBuySell': {
      const buy = row.bestBuyPrice || 0;
      const sell = row.bestSellPrice || 0;
      if (buy === 0 && sell === 0) return <td key={col.key} className={`${base} text-right ${dim}`}>—</td>;
      return (
        <td key={col.key} className={`${base} text-right`}>
          <span className="text-accent-green">{buy > 0 ? Number(buy).toFixed(2) : '—'}</span>
          <span className={dim}> / </span>
          <span className="text-accent-red">{sell > 0 ? Number(sell).toFixed(2) : '—'}</span>
        </td>
      );
    }
    case 'orderId':
      return <td key={col.key} className={`${base} ${muted} font-mono text-[10px]`} title={row.orderId}>{row.orderId || '—'}</td>;
    case 'accountLabel':
      return <td key={col.key} className={`${base} ${muted}`}>{row.accountLabel || row.account}</td>;
    default:
      return <td key={col.key} className={`${base} ${dim}`}>—</td>;
  }
}

// ═══════════════════════════════════════════════════════════════
// ORDERS TABLE — Virtual Scrolling via TableVirtuoso
// ═══════════════════════════════════════════════════════════════

function OrdersTable({ rows, sortCol, sortDir, onSort, onModifyClick, onCancelClick, onTradesClick, isDark, filters = {}, t = {}, columns = ORDER_COLUMNS, onColumnResize }) {
  const filterMap = {
    expiry:  { selected: filters.expiryFilter  || [], setSelected: filters.setExpiryFilter,  options: filters.uniqueExpiries  || [] },
    type:    { selected: filters.typeFilter    || [], setSelected: filters.setTypeFilter,    options: ['All', 'CE', 'PE'] },
    bs:      { selected: filters.bsFilter      || [], setSelected: filters.setBsFilter,      options: ['All', 'BUY', 'SELL'] },
    status:  { selected: filters.statusFilter  || [], setSelected: filters.setStatusFilter,  options: filters.uniqueStatuses  || [] },
    account: { selected: filters.accountFilter || [], setSelected: filters.setAccountFilter, options: filters.uniqueAccountLabels || [] },
  };

  const borderColor = isDark ? '#1e2130' : '#e5e7eb';
  const headerBg = isDark ? '#13151e' : '#f9fafb';
  const headerText = isDark ? '#8b8fa3' : '#6b7280';
  const hoverBg = isDark ? '#1e2130' : '#f0f4ff';

  const tableComponents = useMemo(() => ({
    Table: forwardRef(({ style, ...props }, ref) => (
      <>
        <style>{`table.ord-vt tbody td { border-bottom: 1px solid ${borderColor}; }`}</style>
        <table ref={ref} {...props} className="ord-vt" style={{ ...style, width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 12, minWidth: 1200 }} />
      </>
    )),
    TableRow: forwardRef(({ style, ...props }, ref) => (
      <tr ref={ref} {...props} style={{ ...style, background: 'transparent' }}
        onMouseEnter={e => { e.currentTarget.style.background = hoverBg; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
      />
    )),
  }), [borderColor, hoverBg]);

  if (rows.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center" style={{ color: headerText }}>
        <div className="text-center py-10">
          <div className="text-sm font-medium mb-1">No orders today</div>
          <div className="text-xs opacity-60">Orders will appear here when you place trades</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-x-auto flex flex-col min-h-0">
      <TableVirtuoso
        style={{ flex: 1, minHeight: 0 }}
        data={rows}
        // Track rows by stable identity (order id), not array index — keeps the row you're
        // watching anchored when the set changes underneath you (same fix as Market Watch).
        computeItemKey={(_, row) => row.orderId}
        components={tableComponents}
        overscan={10}
        fixedHeaderContent={() => (
          <tr style={{ background: headerBg }}>
            {columns.map(col => {
              const fm = col.filter ? filterMap[col.filter] : null;
              return (
                <th key={col.key}
                  style={{
                    padding: '5px 6px', textAlign: col.align, fontWeight: 600, fontSize: 10.5,
                    color: headerText, textTransform: 'uppercase', letterSpacing: 0.3,
                    cursor: 'pointer', userSelect: 'none', position: 'relative',
                    borderBottom: `1px solid ${borderColor}`, whiteSpace: 'nowrap',
                    width: col.width, minWidth: col.width,
                    background: sortCol === col.key ? (isDark ? '#1e2240' : '#e8ecff') : headerBg
                  }}
                >
                  <span onClick={() => onSort(col.key)}>{col.label}</span>
                  {sortCol === col.key && <span className="ml-0.5 text-[9px]">{sortDir === 'asc' ? '▲' : '▼'}</span>}
                  {fm && fm.setSelected && (
                    <CheckboxDropdown label={col.label} selected={fm.selected} setSelected={fm.setSelected} options={fm.options} t={t} compact />
                  )}
                  {onColumnResize && <ResizeGrip colKey={col.key} width={col.width} onResize={onColumnResize} />}
                </th>
              );
            })}
            <th style={{ padding: '5px 6px', width: 90, minWidth: 90, borderBottom: `1px solid ${borderColor}`, background: headerBg, textAlign: 'center', fontSize: 10.5, fontWeight: 600, color: headerText, textTransform: 'uppercase' }}>
              Action
            </th>
          </tr>
        )}
        itemContent={(index, row) => (
          <>
            {columns.map(col => renderCell(col, row, isDark))}
            <td className="px-1.5 py-1 text-center whitespace-nowrap">
              <div className="flex gap-1 justify-center">
                {(row.status === 'OPEN' || row.status === 'PENDING' || row.status === 'PARTIAL' || row.status === 'AMO') && (
                  <>
                    <button onClick={() => onModifyClick(row)}
                      className={`px-2 py-0.5 text-[10px] font-semibold rounded cursor-pointer border-none ${isDark ? 'bg-[#1e2240] text-accent-blue' : 'bg-blue-50 text-blue-600'}`}>
                      Modify
                    </button>
                    <button onClick={() => onCancelClick(row)}
                      className={`px-2 py-0.5 text-[10px] font-semibold rounded cursor-pointer border-none ${isDark ? 'bg-[#2e1a1a] text-accent-red' : 'bg-red-50 text-red-600'}`}>
                      Cancel
                    </button>
                  </>
                )}
                {row.filledQuantity > 0 && (
                  <button onClick={() => onTradesClick(row)}
                    className={`px-2 py-0.5 text-[10px] font-semibold rounded cursor-pointer border-none ${isDark ? 'bg-[#1a2e1a] text-accent-green' : 'bg-green-50 text-green-600'}`}>
                    Trades
                  </button>
                )}
              </div>
            </td>
          </>
        )}
      />
    </div>
  );
}

export default OrdersTable;
